import axios from "axios";
import currency from "currency.js";
import i18next from "i18next";
import { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { TFunction } from "react-i18next";
import { MoneyApi } from "go-core/api/types";
import FormatDate, { defaultFormatDate } from "go-core/components/Formatters/FormatDate";
import { AddressApi } from "go-core/types";
import { CustomFieldEntityApi, CustomFieldTemplateApi } from "go-segment/services/types";
import {
	InvoiceApi,
	InvoiceDetailsApi,
	InvoiceIssuerApi,
	InvoiceItemApi,
	InvoiceOrderApi,
	InvoiceReceiverApi,
	InvoiceRecipientApi,
	InvoiceSummaryApi,
	TaxApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { A4WIDTH } from "../../../utils/documentPdf/consts";
import { INVOICE_PDF_TABLE_STYLES } from "./consts";
import { generateInvoicePaymentsTablePdf } from "./invoicePaymentsTable";
import { generateInvoiceSummaryTable } from "./invoiceSummaryTable";
import { generateInvoiceItemsTable } from "./invoiceTable";
import { ExportInvoicePdfConfig } from "./types";

export const exportInvoicePdf = async (
	config: ExportInvoicePdfConfig,
	t: TFunction,
	data: InvoiceItemApi[],
	invoice: InvoiceApi,
	summary: InvoiceSummaryApi,
	taxes: TaxApi[],
	invoiceDetails: InvoiceDetailsApi,
	print?: boolean
) => {
	const table = generateInvoiceItemsTable(
		t,
		config.group ? getGroupedItems(data, config) : data,
		invoice,
		config,
		taxes
	);
	const summaryTable = generateInvoiceSummaryTable(t, summary, taxes, invoice);
	const paymentsTable = generateInvoicePaymentsTablePdf(t, invoice.payments);
	let organizationName: string | undefined = undefined;
	let companyName: string | undefined = undefined;
	let taxIdNo: string | undefined = undefined;
	const title =
		invoice.status === "DRAFT"
			? `${t("enums.invoices.document_type.PROFORMA")} #${invoice.id}`
			: `${t("enums.invoices.document_type.INVOICE")} #${invoice.document_number.full_number}`;

	if ((config?.company && config.company !== "undefined") || (config?.taxIdNo && config.taxIdNo !== "undefined")) {
		if (config?.company && config.company !== "undefined") companyName = config.company;
		if (config?.taxIdNo && config.taxIdNo !== "undefined") taxIdNo = config.taxIdNo;
	} else if (config?.organization && config.organization !== "undefined") {
		organizationName = config?.organization;
	}

	const docDefinition: TDocumentDefinitions = {
		pageOrientation: table.pageOrientation,
		pageMargins: [20, 20, 20, 40],
		footer(currentPage, pageCount) {
			const date = new Date();
			const utc = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
			let footerFirstColumnText = "";

			if (organizationName) footerFirstColumnText += `${i18next.t("lib:common.word.venue")}: ${organizationName}`;
			if (companyName) footerFirstColumnText += `\n ${companyName}`;
			if (taxIdNo) footerFirstColumnText += `\n ${i18next.t("lib:common.word.tax_id_no.title")}: ${taxIdNo}`;
			footerFirstColumnText += `\n ${utc} by GoPOS.pl`;

			return {
				columns: [
					{
						text: `${footerFirstColumnText}`,
						alignment: "left",
						fontSize: 8,
						margin: [20, organizationName ? 10 : 0, 0, 0],
					},
					{
						text: `${i18next.t("lib:common.word.page")} ${currentPage.toString()} ${i18next.t(
							"lib:common.word.of"
						)} ${pageCount}`,
						fontSize: 8,
						alignment: "right",
						margin: [0, 20, 20, 0],
					},
				],
			};
		},
		content: [
			...(await getInfoOnTop(t, invoice, config, invoiceDetails)),
			{
				canvas: [{ type: "line", x1: 0, y1: 0, x2: A4WIDTH - 2 * 20, y2: 0, lineWidth: 1 }],
				margin: [0, 10, 0, 10],
			},
			{
				text: title,
				style: "header",
			},
			{
				canvas: [{ type: "line", x1: 0, y1: 0, x2: A4WIDTH - 2 * 20, y2: 0, lineWidth: 1 }],
				margin: [0, 10, 0, 20],
			},
			...getInformationAboveTable(invoice, t, config.customFields || [], config),
			{
				text: t("common.word.items"),
				style: "label",
				margin: [0, 20, 0, 10],
			},
			{
				style: "table",
				color: "#444",
				table,
				layout: {
					hLineColor: () => "#aaaaaa",
					vLineColor: () => "#aaaaaa",
				},
				margin: [0, 0, 0, 0],
			},
			{
				text: t("lib:common.word.summary"),
				style: "label",
				margin: [0, 20, 0, 10],
			},
			{
				style: "table",
				color: "#444",
				table: summaryTable,
				layout: {
					hLineColor: () => "#aaaaaa",
					vLineColor: () => "#aaaaaa",
				},
				margin: [0, 0, 0, 0],
			},
			paymentsTable
				? [
						{
							text: t("modules.invoice.field.payments.title"),
							style: "label",
							margin: [0, 20, 0, 10],
						},
						{
							style: "table",
							color: "#444",
							table: paymentsTable,
							layout: {
								hLineColor: () => "#aaaaaa",
								vLineColor: () => "#aaaaaa",
							},
							margin: [0, 0, 0, 0],
						},
				  ]
				: "",
			...getInvoiceOrdersContent(invoice.orders, t),
			invoice.issuer_person_name
				? {
						columns: [
							{
								width: "*",
								text: t("modules.invoice.field.without_sign.title"),
								style: "value",
							},
							{
								width: "auto",
								text: `${t("modules.invoice.field.issuer_person_name.description")}: ${
									invoice.issuer_person_name
								}`,
								style: "value",
								alignment: "right",
							},
						],
						margin: [0, 10, 0, 0],
				  }
				: "",
		],
		styles: INVOICE_PDF_TABLE_STYLES,
	};

	const pdfMake = (await import("pdfmake/build/pdfmake")).default;
	const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
	pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts;

	if (print) pdfMake.createPdf(docDefinition).print();
	else pdfMake.createPdf(docDefinition).download(`${title}.pdf`);
};

const getGroupedItems = (items: InvoiceItemApi[], config: ExportInvoicePdfConfig) => {
	const groupedItems: InvoiceItemApi[] = [];

	items.forEach((item) => {
		const indexOfExistingItemWithTax = groupedItems.findIndex((groupedItem) => groupedItem.tax_id === item.tax_id);

		if (indexOfExistingItemWithTax > -1) {
			const totalPriceGross: MoneyApi = {
				amount: currency(groupedItems[indexOfExistingItemWithTax].price_sum_gross?.amount || 0).add(
					item.price_sum_gross?.amount || 0
				).value,
				currency: groupedItems[indexOfExistingItemWithTax].price_sum_gross?.currency || "",
			};
			const totalPriceNet: MoneyApi = {
				amount: currency(groupedItems[indexOfExistingItemWithTax].price_sum_net?.amount || 0).add(
					item.price_sum_net?.amount || 0
				).value,
				currency: groupedItems[indexOfExistingItemWithTax].price_sum_net?.currency || "",
			};
			groupedItems[indexOfExistingItemWithTax] = {
				...groupedItems[indexOfExistingItemWithTax],
				price_net: totalPriceNet,
				price_gross: totalPriceGross,
				price_sum_net: totalPriceNet,
				price_sum_gross: totalPriceGross,
			};
		} else {
			groupedItems.push({
				...item,
				pkwiu: "",
				tax: item.tax,
				quantity: 1,
				measure_type: "ITEM",
				name: config.groupName || "",
				price_net: item.price_sum_net,
				price_gross: item.price_sum_gross,
			});
		}
	});

	return groupedItems;
};

const fetchImageAsDataURL = async (url: string) => {
	const { data } = await axios.get(url, {
		headers: { "Cache-Control": "no-cache" },
		responseType: "arraybuffer",
	});
	return Buffer.from(data, "binary").toString("base64");
};

const getImageDimensions = async (url: string) => {
	const image = new Image();
	image.src = url;

	return new Promise<{ width: number; height: number }>((resolve) => {
		image.onload = () => {
			resolve({
				width: image.width,
				height: image.height,
			});
		};
	});
};

const getInfoOnTop = async (
	t: TFunction,
	invoice: InvoiceApi,
	config: ExportInvoicePdfConfig,
	invoiceDetails: InvoiceDetailsApi
): Promise<Array<Content>> => {
	const content: Content[] = [];

	if (invoiceDetails.logo_link?.default_link) {
		const image = await fetchImageAsDataURL(invoiceDetails.logo_link.default_link);
		const { width: originalWidth, height: originalHeight } = await getImageDimensions(
			invoiceDetails.logo_link.default_link
		);
		const newHeight = 100;
		const newWidth = (originalWidth / originalHeight) * newHeight;
		content.push({
			image: `data:image/jpeg;base64,${image}`,
			height: newHeight,
			width: newWidth,
		});
	}

	let issueDateTitle = `${t("modules.invoice.field.issued_at.title")}: ${FormatDate(
		invoice.dates.issue_date,
		undefined,
		false,
		defaultFormatDate
	)}`;

	if (invoice.place_of_issue) {
		issueDateTitle += `, ${invoice.place_of_issue}`;
	}

	if (config.duplicate && config.duplicateDate) {
		content.push({
			text: `${t("modules.invoice.field.duplicate.title")} ${FormatDate(
				config.duplicateDate,
				undefined,
				false,
				defaultFormatDate
			)}`,
			alignment: "center",
			style: "label",
		});
	}

	content.push({
		text: issueDateTitle,
		style: "rightCorner",
	});

	return content;
};

const getAddressInfo = (address: AddressApi) => {
	const result: string[] = [];
	const zipCodeParts: string[] = [];
	let street = "";

	if (address.zip_code) {
		zipCodeParts.push(address.zip_code);
	}

	if (address.city) {
		zipCodeParts.push(address.city);
	}

	result.push(zipCodeParts.join(" "));

	if (address.street) {
		street += address.street;
	}
	if (address.build_nr) {
		street += ` ${address.build_nr}`;
	}
	if (address.flat_nr) {
		street += `/${address.flat_nr}`;
	}

	result.push(street);

	return result.join("\n");
};

const getPersonData = (
	person: InvoiceIssuerApi | InvoiceRecipientApi | InvoiceReceiverApi | undefined | null,
	t: TFunction
) => {
	if (!person) return "";

	const result: string[] = [person.name];

	if (person.address) {
		const address = getAddressInfo(person.address);
		result.push(address);
	}

	if (person.tax_id_no) {
		result.push(`${t("lib:common.word.tax_id_no.title")}: ${person.tax_id_no}`);
	}

	if (person.contact?.email) {
		result.push(`${t("lib:common.word.email")}: ${person.contact.email}`);
	}

	return result.join("\n");
};

const getInformationAboveTable = (
	invoice: InvoiceApi,
	t: TFunction,
	customFieldsConfig: CustomFieldTemplateApi[],
	config: ExportInvoicePdfConfig
): Array<Content> => {
	const content: Content[] = [];

	if (invoice.dates?.sale_date) {
		content.push({
			text: `${t("modules.invoice.field.sale_date.title")}: ${FormatDate(
				invoice.dates.sale_date,
				undefined,
				false,
				defaultFormatDate
			)}`,
			style: "label",
			alignment: "center",
		});
	}

	if (invoice.dates?.pay_date) {
		content.push({
			text: `${t("modules.invoice.field.pay_date.title")}: ${FormatDate(
				invoice.dates.pay_date,
				undefined,
				false,
				defaultFormatDate
			)}`,
			style: "label",
			alignment: "center",
		});
	}

	if (content.length > 0) {
		content.push({ text: "", style: "separator" });
	}

	return [
		...content,
		{
			columns: [
				{
					width: "*",
					text: t("modules.invoice.field.seller.title"),
					style: "label",
				},
				{
					width: "auto",
					text: t("modules.invoice.field.recipient.title"),
					style: "label",
					alignment: "right",
				},
			],
		},
		{
			columns: [
				{
					width: "*",
					text: getPersonData(invoice.issuer, t),
					style: "value",
				},
				{
					width: "auto",
					text: getPersonData(invoice.recipient, t),
					style: "value",
					alignment: "right",
				},
			],
		},
		{
			text: "",
			style: "separator",
		},
		{
			columns: [
				{
					width: "*",
					text: "",
					style: "label",
				},
				{
					width: "auto",
					text: invoice.receiver ? t("modules.invoice.field.receiver.title") : "",
					style: "label",
					alignment: "right",
				},
			],
		},
		{
			columns: [
				{
					width: "*",
					text: "",
					style: "value",
				},
				{
					width: "auto",
					text: getPersonData(invoice.receiver, t),
					style: "value",
					alignment: "right",
				},
			],
		},
		...getCustomFieldsColumn(customFieldsConfig, invoice.custom_fields, !!config.printCustomFields),
	];
};

const findCustomField = (
	customFieldConfig: CustomFieldTemplateApi,
	customFields: CustomFieldEntityApi[],
	print: boolean
) => {
	if (!print) return undefined;

	return customFields.find((cf) => cf.slug === customFieldConfig.slug);
};

const getCustomFieldsColumn = (
	customFieldsConfig: CustomFieldTemplateApi[],
	customFields: CustomFieldEntityApi[],
	print: boolean
): Array<Content> => {
	const content: Content[] = [];

	if (!print) return content;

	if (customFieldsConfig.length > 0) {
		customFieldsConfig.forEach((customFieldConfig) => {
			const customField = findCustomField(customFieldConfig, customFields, print)?.value || "";
			content.push(
				{
					text: "",
					style: "separator",
				},
				{
					columns: [
						{
							width: "*",
							text: "",
							style: "label",
						},
						{
							width: "auto",
							text: customField ? customFieldConfig.name : "",
							style: "label",
							alignment: "right",
						},
					],
				},
				{
					columns: [
						{
							width: "*",
							text: "",
							style: "value",
						},
						{
							width: "auto",
							text: findCustomField(customFieldConfig, customFields, print)?.value || "",
							style: "value",
							alignment: "right",
						},
					],
				}
			);
		});
	}

	return content;
};

const getInvoiceOrdersContent = (orders: InvoiceOrderApi[], t: TFunction): Content[] => {
	if (orders.length === 0) return [];

	const ordersNumbers = orders.map((order) => order.order_external_number);
	const title = `${t("modules.invoice.field.invoice_created_for_orders.title")}: ${ordersNumbers
		.map((number) => number)
		.join(", ")}`;

	return [
		{
			text: title,
			style: "value",
			margin: [0, 10, 0, 0],
			alignment: "center",
		},
	];
};
